import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import "datatables.net-select-dt";
import "datatables.net-responsive-dt";
import "datatables.net-dt/css/dataTables.dataTables.css";

// Inicializa o DataTables com o core do Vanilla JS
DataTable.use(DT);

// Dicionário de idiomas para o DataTables
const dataTableLanguages = {
  "pt-AO": {
    sEmptyTable: "Nenhum registo encontrado",
    sInfo: "Mostrando de _START_ até _END_ de _TOTAL_ registos",
    sInfoEmpty: "Mostrando 0 até 0 de 0 registos",
    sInfoFiltered: "(Filtrados de _MAX_ registos)",
    sLengthMenu: "Exibir _MENU_ resultados por página",
    sLoadingRecords: "Carregando...",
    sProcessing: "Processando...",
    sZeroRecords: "Nenhum registo encontrado",
    sSearch: "Pesquisar",
    oPaginate: { sNext: "Próximo", sPrevious: "Anterior", sFirst: "Primeiro", sLast: "Último" },
    oAria: {
      sSortAscending: ": Ordenar colunas de forma ascendente",
      sSortDescending: ": Ordenar colunas de forma descendente",
    },
  },
  "en-US": {
    sEmptyTable: "No records found",
    sInfo: "Showing _START_ to _END_ of _TOTAL_ records",
    sInfoEmpty: "Showing 0 to 0 of 0 records",
    sInfoFiltered: "(filtered from _MAX_ records)",
    sLengthMenu: "Show _MENU_ results per page",
    sLoadingRecords: "Loading...",
    sProcessing: "Processing...",
    sZeroRecords: "No records found",
    sSearch: "Search",
    oPaginate: { sNext: "Next", sPrevious: "Previous", sFirst: "First", sLast: "Last" },
    oAria: {
      sSortAscending: ": sort columns ascending",
      sSortDescending: ": sort columns descending",
    },
  },
  "fr-FR": {
    sEmptyTable: "Aucun enregistrement trouvé",
    sInfo: "Affichage de _START_ à _END_ sur _TOTAL_ enregistrements",
    sInfoEmpty: "Affichage de 0 à 0 sur 0 enregistrement",
    sInfoFiltered: "(filtré de _MAX_ enregistrements)",
    sLengthMenu: "Afficher _MENU_ résultats par page",
    sLoadingRecords: "Chargement...",
    sProcessing: "Traitement...",
    sZeroRecords: "Aucun enregistrement trouvé",
    sSearch: "Rechercher",
    oPaginate: { sNext: "Suivant", sPrevious: "Précédent", sFirst: "Premier", sLast: "Dernier" },
    oAria: {
      sSortAscending: ": trier les colonnes par ordre croissant",
      sSortDescending: ": trier les colonnes par ordre décroissant",
    },
  },
  "es-ES": {
    sEmptyTable: "No se encontraron registros",
    sInfo: "Mostrando de _START_ a _END_ de _TOTAL_ registros",
    sInfoEmpty: "Mostrando 0 a 0 de 0 registros",
    sInfoFiltered: "(filtrados de _MAX_ registros)",
    sLengthMenu: "Mostrar _MENU_ resultados por página",
    sLoadingRecords: "Cargando...",
    sProcessing: "Procesando...",
    sZeroRecords: "No se encontraron registros",
    sSearch: "Buscar",
    oPaginate: { sNext: "Siguiente", sPrevious: "Anterior", sFirst: "Primero", sLast: "Último" },
    oAria: {
      sSortAscending: ": ordenar columnas de forma ascendente",
      sSortDescending: ": ordenar columnas de forma descendente",
    },
  },
  "zh-CN": {
    sEmptyTable: "未找到记录",
    sInfo: "显示第 _START_ 至 _END_ 条，共 _TOTAL_ 条",
    sInfoEmpty: "显示 0 至 0 条，共 0 条",
    sInfoFiltered: "（从 _MAX_ 条中过滤）",
    sLengthMenu: "每页显示 _MENU_ 条",
    sLoadingRecords: "加载中...",
    sProcessing: "处理中...",
    sZeroRecords: "未找到记录",
    sSearch: "搜索",
    oPaginate: { sNext: "下一页", sPrevious: "上一页", sFirst: "首页", sLast: "末页" },
    oAria: {
      sSortAscending: ": 升序排列",
      sSortDescending: ": 降序排列",
    },
  },
  "ar-SA": {
    sEmptyTable: "لا توجد سجلات",
    sInfo: "عرض _START_ إلى _END_ من _TOTAL_ سجل",
    sInfoEmpty: "عرض 0 إلى 0 من 0 سجل",
    sInfoFiltered: "(تمت التصفية من _MAX_ سجل)",
    sLengthMenu: "عرض _MENU_ نتيجة لكل صفحة",
    sLoadingRecords: "جار التحميل...",
    sProcessing: "جار المعالجة...",
    sZeroRecords: "لا توجد سجلات",
    sSearch: "بحث",
    oPaginate: { sNext: "التالي", sPrevious: "السابق", sFirst: "الأول", sLast: "الأخير" },
    oAria: {
      sSortAscending: ": ترتيب تصاعدي",
      sSortDescending: ": ترتيب تنازلي",
    },
  },
};

const MyDataTable = ({ columns, data }) => {
  // Obtém o idioma atual do estado global do Redux
  const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");

  // Memoriza as opções para evitar re-renderizações desnecessárias da tabela
  const options = useMemo(() => ({
    language: dataTableLanguages[language] || dataTableLanguages["pt-AO"],
    responsive: true,
    select: true,
    ordering: true,
  }), [language]);

  return (
    <DataTable
      columns={columns}
      data={data}
      options={options}
      className="table table-striped table-bordered table-hover text-nowrap w-100 align-middle"
    />
  );
};

export default MyDataTable;